import { useSyncExternalStore } from 'react';

import posthog from 'posthog-js';

export type FlagsStatus = 'loading' | 'ready' | 'off';

const LOAD_TIMEOUT_MS = 2000;

let snapshot: { status: FlagsStatus } = { status: 'off' };
let loadTimeout: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

function setStatus(status: FlagsStatus): void {
  snapshot = { status };
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = (): { status: FlagsStatus } => snapshot;

export function useFlagsStatus(): FlagsStatus {
  return useSyncExternalStore(subscribe, getSnapshot).status;
}

export function notifyFlags(): void {
  setStatus(snapshot.status);
}

/**
 * Call when PostHog starts loading flags for a person: init, an `identify` that changes the distinct id
 * or a `reset`. It is the only way out of `off`.
 */
export function markFlagsReloading(): void {
  clearTimeout(loadTimeout);
  setStatus('loading');
  loadTimeout = setTimeout(() => {
    if (snapshot.status === 'loading') setStatus('off');
  }, LOAD_TIMEOUT_MS);
}

/**
 * Call right after `posthog.init`. Only server responses carry `errorsLoading`, so other callbacks
 * (overrides, early access) never mark the flags of the previous person as ready. After `off`, late
 * responses are ignored until the next `markFlagsReloading`.
 */
export function watchFlags(): void {
  markFlagsReloading();
  posthog.onFeatureFlags((_flags, _variants, context) => {
    if (snapshot.status === 'off') return;
    if (context?.errorsLoading === false) {
      clearTimeout(loadTimeout);
      setStatus('ready');
    } else if (context?.errorsLoading && snapshot.status === 'loading') {
      clearTimeout(loadTimeout);
      setStatus('off');
    }
  });
}
